import i18n from '@/lib/i18n';
import { getFriendlyErrorCode } from '@/lib/friendlyError';
import { mapApiErrorMessage } from '@/lib/apiErrors';
import type { useToast } from '@/contexts/ToastContext';

type ToastApi = ReturnType<typeof useToast>;

const WORKER_MARKERS = [
  'firebase_service_account_json',
  'sync-dev-vars',
  'firebaseconfigured',
  'not configured'
];

const MESSAGE_KEY_MAP: Record<string, string> = {
  'Not found': 'errors.unavailable',
  'No lines': 'journals.balanceNoLines',
  'Line cannot have both debit and credit': 'journals.balanceBothSides',
  'No amounts': 'journals.balanceNoAmounts',
  'Debits must equal credits': 'journals.balanceUnequal',
  'Opening balances must balance': 'coa.openingMustBalance',
  'Amount must be positive': 'cashbook.amountRequired',
  'Fiscal period is closed': 'coa.periodNotOpen',
  'Set opening treasury first': 'cashbook.noOpening',
  'Cannot edit opening after cashbook entries exist': 'cashbook.openingEditLocked',
  'Cannot edit opening after cashbook or debt entries exist': 'cashbook.openingEditLocked',
  'Payment exceeds outstanding debts': 'cashbook.debts.errors.overpayment',
  'Payment exceeds left in drawer': 'cashbook.debts.errors.exceedsDrawer',
  'Cashbook data changed — refresh and try again': 'cashbook.debts.errors.staleData',
  'Cashbook restore in progress — try again when restore finishes': 'cashbook.debts.errors.restoreInProgress',
  'Cannot void — payments already recorded against this debt': 'cashbook.debts.errors.cannotVoidIncurred',
  'Cannot remove opening debt line — payments already recorded against it':
    'cashbook.debts.errors.cannotRemoveOpeningPaid',
  'Use Pay debt to record debt payments': 'cashbook.debts.errors.usePayDebt',
  'Use Add debt with Borrowed money to record loan cash':
    'cashbook.debts.errors.useAddBorrowedMoney',
  'Invalid debt category': 'cashbook.debts.errors.invalidCategory',
  'Not signed in': 'errors.notSignedIn',
  'Request failed': 'errors.requestFailed',
  'Invalid API response': 'errors.invalidResponse',
  'VITE_API_URL is not configured': 'errors.apiNotConfigured',
  'Empty file': 'import.errors.emptyFile',
  'No sheet in workbook': 'import.errors.noSheet',
  'Empty sheet': 'import.errors.emptySheet',
  'Unsupported format. Use CSV or Excel (.xlsx).': 'import.errors.unsupportedFormat',
  'Parse failed': 'import.errors.parseFailed',
  'Validation failed': 'import.errors.validationFailed',
  'Opening treasury already set': 'cashbook.openingAlreadySet',
  'Opening amount must be greater than zero': 'cashbook.openingAmountRequired',
  'Opening treasury is not set': 'cashbook.noOpening',
  'Insufficient permissions': 'errors.permissionDenied',
  'Cannot set opening after cashbook entries exist': 'cashbook.openingEditLocked',
  'Entry date is before fiscal period start': 'cashbook.entryDateBeforePeriod',
  'Entry date is after fiscal period end': 'cashbook.entryDateAfterPeriod',
  'Entry date is before cashbook opening date': 'cashbook.entryDateBeforeOpening',
  'Fiscal period not found': 'coa.periodNotOpen',
  'Date range exceeds': 'cashbook.viewRangeTooWide',
  'Unauthorized': 'errors.notSignedIn',
  'Email already in use': 'settings.addUserEmailInUse',
  'Invalid email': 'settings.addUserInvalidEmail',
  'Password must be at least 6 characters': 'settings.addUserWeakPassword',
  'Cannot assign owner role': 'errors.permissionDenied',
  'Cannot change owner role': 'errors.permissionDenied',
  'Cannot change owner permissions': 'errors.permissionDenied',
  'Cannot remove yourself': 'errors.permissionDenied',
  'Cannot remove the last owner': 'errors.permissionDenied',
  'Member not found': 'errors.unavailable',
  'Import failed': 'import.errors.importFailed',
  'Save failed': 'journals.saveFailed',
  'Post failed': 'journals.postFailed',
  'Invalid JSON': 'connectors.invalidJson',
  'Test failed': 'connectors.testFailed',
  'Sync failed': 'connectors.syncFailed',
  'Commit failed': 'connectors.commitFailed',
  'No PayVox sales found': 'cashbook.payvox.errors.notFound',
  'All PayVox sales in this range are already imported': 'cashbook.payvox.errors.alreadyImported',
  'No importable PayVox sales in this range': 'cashbook.payvox.errors.noneImportable',
  'PayVox import validation failed': 'cashbook.payvox.errors.failed',
  'End date must be on or after start date': 'cashbook.payvox.errors.dateOrder',
  'Sales category is not configured': 'cashbook.payvox.errors.noSalesCategory'
};

export function resolveUserErrorMessage(error: unknown, fallbackKey = 'errors.generic'): string {
  if (error instanceof Error && error.message) {
    return resolveMessage(error.message, fallbackKey);
  }
  const code = getFriendlyErrorCode(error);
  const key =
    code === 'permissionDenied'
      ? 'errors.permissionDenied'
      : code === 'network'
        ? 'errors.network'
        : code === 'unavailable'
          ? 'errors.unavailable'
          : fallbackKey;
  return i18n.t(key);
}

function resolveMessage(raw: string, fallbackKey: string): string {
  const mapped = mapApiErrorMessage(raw);
  if (mapped !== raw) return mapped;
  for (const [needle, key] of Object.entries(MESSAGE_KEY_MAP)) {
    if (raw === needle || raw.includes(needle)) return i18n.t(key);
  }
  if (/Too many rows/i.test(raw)) return i18n.t('import.errors.tooManyRows');
  if (/File too large/i.test(raw)) return i18n.t('import.errors.fileTooLarge');
  return raw || i18n.t(fallbackKey);
}

export function toastError(
  toast: ToastApi,
  error: unknown,
  toastId: string,
  fallbackKey = 'errors.generic'
): void {
  if (error instanceof Error && error.message) {
    const resolved = resolveMessage(error.message, fallbackKey);
    const useFallback =
      resolved === error.message &&
      MESSAGE_KEY_MAP[error.message] === undefined &&
      !WORKER_MARKERS.some((m) => error.message.toLowerCase().includes(m));
    toast.error(useFallback ? i18n.t(fallbackKey) : resolved, toastId);
    return;
  }
  const code = getFriendlyErrorCode(error);
  const key =
    code === 'permissionDenied'
      ? 'errors.permissionDenied'
      : code === 'network'
        ? 'errors.network'
        : code === 'unavailable'
          ? 'errors.unavailable'
          : fallbackKey;
  toast.error(i18n.t(key), toastId);
}

export function mapJournalBalanceError(error: string | undefined): string {
  if (!error) return i18n.t('errors.generic');
  return resolveMessage(error, 'errors.generic');
}
